import { useEffect, useRef } from 'react';
import { CallState } from '../../hooks/useWebRTC';
import TurnstileCaptcha from './TurnstileCaptcha';
import { useCallAudio } from '../../hooks/useCallAudio';

interface CallModalProps {
  isOpen: boolean;
  onClose: () => void;
  callState: CallState;
  error: string | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isScreenSharing: boolean;
  callerName: string;
  onCallerNameChange: (name: string) => void;
  onCaptchaVerify: (token: string | null) => void;
  onStartCall: () => void;
  onEndCall: () => void;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  isInCall: boolean;
  captchaVerified: boolean;
}

export default function CallModal({
  isOpen,
  onClose,
  callState,
  error,
  localStream,
  remoteStream,
  isAudioEnabled,
  isVideoEnabled,
  isScreenSharing,
  callerName,
  onCallerNameChange,
  onCaptchaVerify,
  onStartCall,
  onEndCall,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  isInCall: _isInCall,
  captchaVerified,
}: CallModalProps) {
  void _isInCall; // Used by parent for state tracking
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const { playRingtone, stopRingtone } = useCallAudio();

  // Play ringing sound while waiting for answer
  useEffect(() => {
    if (callState === 'ringing') {
      playRingtone();
    } else {
      stopRingtone();
    }

    return () => {
      stopRingtone();
    };
  }, [callState, playRingtone, stopRingtone]);

  // Attach streams to video elements
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  if (!isOpen) return null;

  const showPreCall = callState === 'idle' || callState === 'error';
  const showCalling = callState === 'requesting' || callState === 'ringing';
  const showConnected = callState === 'connecting' || callState === 'connected';
  const showEnded = callState === 'ended';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 bg-surface-light dark:bg-surface-darker rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {showPreCall && 'Start a Call'}
            {showCalling && 'Calling...'}
            {showConnected && 'On Call'}
            {showEnded && 'Call Ended'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Pre-call: Name + Captcha */}
          {showPreCall && (
            <div className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Your Name (optional)
                </label>
                <input
                  type="text"
                  value={callerName}
                  onChange={(e) => onCallerNameChange(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-center">
                <TurnstileCaptcha onVerify={onCaptchaVerify} />
              </div>

              <button
                onClick={onStartCall}
                disabled={!captchaVerified}
                className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PhoneIcon />
                Start Call
              </button>

              <p className="text-sm text-center text-neutral-500 dark:text-neutral-400">
                You'll be connected via voice. Video can be enabled during the call.
              </p>
            </div>
          )}

          {/* Calling state */}
          {showCalling && (
            <div className="text-center py-12">
              <div className="animate-pulse mb-6">
                <div className="w-24 h-24 mx-auto rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <PhoneIcon className="w-12 h-12 text-primary-600 dark:text-primary-400" />
                </div>
              </div>
              <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-2">
                {callState === 'requesting' ? 'Connecting...' : 'Ringing...'}
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Waiting for Wez to answer
              </p>
              <button
                onClick={onEndCall}
                className="mt-6 btn bg-red-600 hover:bg-red-700 text-white"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Connected state */}
          {showConnected && (
            <div className="space-y-4">
              {/* Video area */}
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                {/* Remote video/audio */}
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />

                {/* Local video PiP */}
                {(isVideoEnabled || isScreenSharing) && (
                  <div className="absolute bottom-4 right-4 w-32 aspect-video bg-neutral-900 rounded-lg overflow-hidden shadow-lg">
                    <video
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Audio-only indicator */}
                {!remoteStream?.getVideoTracks().length && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-24 h-24 mx-auto rounded-full bg-neutral-800 flex items-center justify-center mb-4">
                        <span className="text-4xl">W</span>
                      </div>
                      <p className="text-white">Wez Ireland</p>
                      <p className="text-sm text-neutral-400">
                        {callState === 'connecting' ? 'Connecting...' : 'Voice call'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Call controls */}
              <div className="flex justify-center gap-4">
                <button
                  onClick={onToggleAudio}
                  className={`p-4 rounded-full transition-colors ${
                    isAudioEnabled
                      ? 'bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                  aria-label={isAudioEnabled ? 'Mute' : 'Unmute'}
                >
                  {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
                </button>

                <button
                  onClick={onToggleVideo}
                  className={`p-4 rounded-full transition-colors ${
                    isVideoEnabled
                      ? 'bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600'
                      : 'bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600'
                  }`}
                  aria-label={isVideoEnabled ? 'Stop video' : 'Start video'}
                >
                  {isVideoEnabled ? <VideoIcon /> : <VideoOffIcon />}
                </button>

                <button
                  onClick={onToggleScreenShare}
                  className={`p-4 rounded-full transition-colors ${
                    isScreenSharing
                      ? 'bg-primary-600 hover:bg-primary-700 text-white'
                      : 'bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600'
                  }`}
                  aria-label={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                >
                  <ScreenShareIcon />
                </button>

                <button
                  onClick={onEndCall}
                  className="p-4 rounded-full bg-red-600 hover:bg-red-700 text-white"
                  aria-label="End call"
                >
                  <PhoneOffIcon />
                </button>
              </div>
            </div>
          )}

          {/* Call ended */}
          {showEnded && (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-6">
                <PhoneOffIcon className="w-12 h-12 text-neutral-500" />
              </div>
              <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-4">
                Call ended
              </p>
              <button onClick={onClose} className="btn-primary">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Icons
function CloseIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function PhoneIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function PhoneOffIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 3h5m0 0v5m0-5l-6 6M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function VideoOffIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
    </svg>
  );
}

function ScreenShareIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}
